import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { can } from "src/Common/function";
import UserService from "src/Services/API/user.service";
import WOService from "src/Services/API/wo.service";
import useWoCustomerActivity from "src/hooks/useWoCustomerActivity";

const REFRESH_MS = 60 * 1000;

// Fire this to refresh the badge now (e.g. after opening a work order clears
// its proposal responses) instead of waiting for the next poll.
export const PENDING_REQUESTS_REFRESH_EVENT = "pending-requests-refresh";

/**
 * Number of customer items needing staff attention, for the red badge on the
 * side menu's Work Orders / Customer Requests items: requests waiting for
 * approval (status 0, the rows the Work Orders list highlights pink) plus
 * customer proposal responses (signed / declined) no staff member has opened.
 *
 * Uses the work order list endpoint with a one-row page and reads its
 * totalCount, so it is scoped to the branches the user can see, same as the
 * list. Re-checked on every page change (e.g. after approving one), every
 * minute, and when the browser tab regains focus.
 */
// Returns { all, requests }: `all` adds the work orders with customer chat
// messages this person hasn't read (the collapsed Work Orders badge);
// `requests` is requests + proposal responses only (Customer Requests).
function usePendingRequestCount() {
  const location = useLocation();
  const [count, setCount] = useState({ all: 0, requests: 0 });
  const allowed = can("wo:read");

  const refresh = useCallback(() => {
    const user = UserService.getCurrentUser();
    if (!allowed || !user) return;
    const countOf = (criteria) =>
      WOService.getAllWorkOrders(user.id, "", 0, criteria, null, 0, 1, 0).then(
        (response) => Number(response.data?.totalCount) || 0,
      );
    Promise.all([countOf({ needsAttention: true, messages: true }), countOf({ needsAttention: true })])
      .then(([all, requests]) => setCount({ all, requests }))
      .catch(() => {});
  }, [allowed]);

  useEffect(() => {
    refresh();
  }, [refresh, location.pathname, location.search]);

  // A customer request, proposal response or chat message arrives: count it now.
  useWoCustomerActivity(() => refresh());

  useEffect(() => {
    const timer = setInterval(refresh, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(PENDING_REQUESTS_REFRESH_EVENT, refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(PENDING_REQUESTS_REFRESH_EVENT, refresh);
    };
  }, [refresh]);

  return count;
}

export default usePendingRequestCount;
